/**
 * A DRAWER — the same idea as the centre-screen sheet, but slid in from an
 * edge so the list behind it stays put.
 *
 * Anthony liked how WeRV opens a record: you click it and a panel comes in
 * from the side while the page behind it holds its place, so you keep your
 * spot in the list. This is that, for Shop Desk.
 *
 * The chrome is deliberately thin: the head is a slim bar carrying only the
 * edge picker and the way out, so the CONTENT owns its own header — an
 * avatar and a name, a wizard's step rail — and gets the full width to
 * breathe in. Callers render their header in the body; the drawer just
 * frames it.
 *
 * WHICH EDGE IS A PREFERENCE, remembered in localStorage the same way the
 * tiles/rows view is. The picker sits in the bar, and flipping it MOVES THE
 * OPEN DRAWER right then, so the choice is its own preview.
 */

import { esc } from "./dom.ts";

// Left and right slide in from an edge; middle opens as a centred modal.
// Top and bottom are gone — an up/down drawer wasted the wide screen and
// nobody reached for it. An old "top"/"bottom" left in a browser's storage
// is not in this list, so drawerOrient() below falls it back to "right".
export type Orient = "left" | "middle" | "right";
const ORIENTS: readonly Orient[] = ["left", "middle", "right"];
const KEY = "drawer:orient";
const BACK = "drawer-back";

/**
 * DRAWER MODE IS A DESKTOP THING, AND ONLY A DESKTOP THING.
 *
 * Anthony: "add drawer mode, but only for full screen desktop mode."
 *
 * This is the fence the first version of this system did not have. It
 * shipped to live applying everywhere, and on 2026-09-14 Anthony had the
 * whole thing pulled back out (bdef49d). A panel at the edge earns its
 * place by leaving something useful beside it — the day in the middle,
 * the list you came from. On a narrow window there is nothing beside it,
 * so the panel is a page wearing a costume: same content, less room for
 * it, plus a bar and a backdrop nobody asked for.
 *
 * ── WHY 1000px AND A FINE POINTER ───────────────────────────────────
 *
 * 1000px is not a new number. It is the line the shell already draws in
 * app.css: at 1000px the panel stops lying on top of the middle and takes
 * its own space beside it. That IS the question being asked here — is
 * there room for two things — so asking it twice with two different
 * numbers would be two answers waiting to disagree.
 *
 * `pointer: fine` is what keeps the shop tablet out. A tablet in landscape
 * clears 1000px and is still not a desktop: the panel's drag handle needs
 * a mouse, and the day beside it is unreadable at arm's length on a bench.
 * "Desktop" in the request means a desktop, not a wide screen.
 */
const DESKTOP = "(min-width: 1000px) and (pointer: fine)";

/**
 * Is the window a full-screen desktop right now? Everything that opens a
 * panel asks this first, so there is ONE definition of drawer mode rather
 * than a breakpoint copied into each caller.
 *
 * FALSE IS THE SAFE ANSWER. Where matchMedia is missing or throws, the
 * plain page always works and the panel might not, so a broken query lands
 * on the behaviour that cannot be broken.
 */
export function drawerMode(): boolean {
  try {
    return window.matchMedia(DESKTOP).matches;
  } catch {
    return false;
  }
}

/**
 * Tell me when that answer changes — a window snapped to half the screen,
 * a tablet turned on its side, a monitor unplugged.
 *
 * The router needs this. Without it, dragging a window narrow leaves the
 * panel open at a width the layout no longer gives it space for, and
 * dragging it wide leaves a full-page screen with the day nowhere. The
 * mode is not a boot-time decision, so it cannot be read only at boot.
 *
 * `addListener` is the fallback for Safari before 14, which is old enough
 * to be rare and young enough to still be on a bench iPad somewhere.
 */
export function onDrawerModeChange(fn: () => void): void {
  let mq: MediaQueryList;
  try {
    mq = window.matchMedia(DESKTOP);
  } catch {
    return;
  }
  if (typeof mq.addEventListener === "function") mq.addEventListener("change", () => fn());
  else if (typeof mq.addListener === "function") mq.addListener(() => fn());
}

/** Which edge the drawer comes in from. The right unless somebody moved it. */
export function drawerOrient(): Orient {
  try {
    const v = localStorage.getItem(KEY);
    return (ORIENTS as readonly string[]).includes(v ?? "") ? (v as Orient) : "right";
  } catch {
    return "right";
  }
}

/**
 * ONE PREFERENCE, TWO PANELS.
 *
 * The side shell (src/ui/shell.ts) comes in from the same edge out of the
 * same key, and its picker is this picker. Moving one has to move the
 * other or the two disagree the moment you use both in a session.
 *
 * Reached by id rather than by importing the shell, because the shell
 * imports this file for the picker and a cycle between the two would be a
 * worse problem than a selector. The `data-shell` attribute on the root is
 * what the stylesheet reads to give the middle its margin, so it moves too.
 */
export function setDrawerOrient(o: Orient): void {
  try { localStorage.setItem(KEY, o); } catch { /* nothing to do */ }
  document.getElementById(BACK)?.setAttribute("data-orient", o);
  const shell = document.getElementById("shell-back");
  if (shell) {
    shell.setAttribute("data-orient", o);
    document.documentElement.setAttribute("data-shell", o);
  }
}

export function isDrawerOpen(): boolean {
  return !!document.getElementById(BACK);
}

// A filled bar on the side the drawer comes from with an arrow showing the
// way it slides in; the middle glyph is a panel floating centred.
const EDGE: Record<Orient, string> = {
  left: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
     stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="6" height="16" rx="1.4" fill="currentColor" stroke="none"/><path d="M13 12h7M16 9l-3 3 3 3"/></svg>`,
  middle: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
     stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="1.6" opacity=".4"/><rect x="8" y="8" width="8" height="8" rx="1.4" fill="currentColor" stroke="none"/></svg>`,
  right: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
     stroke-linecap="round" stroke-linejoin="round"><rect x="15" y="4" width="6" height="16" rx="1.4" fill="currentColor" stroke="none"/><path d="M11 12H4M8 9l3 3-3 3"/></svg>`,
};
const EDGE_LABEL: Record<Orient, string> = {
  left: "Open from the left", middle: "Open in the middle",
  right: "Open from the right",
};

/** The picker: four buttons, one per edge. Lives in the drawer bar. */
export function orientPickerHtml(): string {
  const cur = drawerOrient();
  return `<div class="orient-pick" role="group" aria-label="Which edge the drawer opens from">
    ${ORIENTS.map((o) =>
      `<button type="button" data-orient="${o}" class="${o === cur ? "on" : ""}"
        title="${EDGE_LABEL[o]}" aria-label="${EDGE_LABEL[o]}"
        aria-pressed="${o === cur}">${EDGE[o]}</button>`).join("")}
  </div>`;
}

export function wireOrientPicker(root: ParentNode): void {
  root.querySelectorAll<HTMLButtonElement>(".orient-pick button").forEach((b) =>
    b.addEventListener("click", () => {
      const o = b.dataset["orient"] as Orient;
      if (o === drawerOrient()) return;
      setDrawerOrient(o);
      root.querySelectorAll<HTMLButtonElement>(".orient-pick button").forEach((x) => {
        const on = x.dataset["orient"] === o;
        x.classList.toggle("on", on);
        x.setAttribute("aria-pressed", String(on));
      });
    }));
}

export interface DrawerOpts {
  /** The body HTML. Render your own header inside it. */
  body: string;
  /** The sticky action row's HTML (buttons). Omitted, there is no action row. */
  acts?: string;
  /** A label for the whole panel, for a screen reader. */
  label?: string;
}

let onClose: (() => void) | null = null;
let teardown: (() => void) | null = null;

/**
 * Open the drawer. Replaces any drawer already open. `close` runs whenever
 * it shuts — by the X, the backdrop, Escape, or a route change.
 */
export function openDrawer(opts: DrawerOpts, close?: () => void): void {
  document.getElementById(BACK)?.remove();
  onClose = close ?? null;
  const orient = drawerOrient();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back is-drawer" id="${BACK}" data-orient="${orient}">
      <div class="sheet drawer-panel" role="dialog" aria-modal="true"
           aria-label="${esc(opts.label ?? "Details")}" tabindex="-1">
        <div class="drawer-bar">
          ${orientPickerHtml()}
          <button class="sheet-x" id="drawer-x" aria-label="Close">&times;</button>
        </div>
        <div class="sheet-body" id="drawer-body">${opts.body}</div>
        <div class="sheet-acts" id="drawer-acts" ${opts.acts ? "" : "hidden"}>${opts.acts ?? ""}</div>
        <div class="msg-slot" id="drawer-msg"></div>
      </div>
    </div>`);

  const back = document.getElementById(BACK);
  if (!back) return;
  wireOrientPicker(back);

  document.getElementById("drawer-x")?.addEventListener("click", () => closeDrawer());
  back.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === BACK) closeDrawer();
  });
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeDrawer(); };
  const onHash = () => closeDrawer();
  document.addEventListener("keydown", onKey);
  window.addEventListener("hashchange", onHash);
  teardown = () => {
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("hashchange", onHash);
  };

  const first = back.querySelector<HTMLElement>(
    ".sheet-body input, .sheet-body textarea, .sheet-body select");
  (first ?? back.querySelector<HTMLElement>(".drawer-panel"))?.focus?.();
}

/**
 * Re-render the drawer's body in place, without sliding a fresh panel in —
 * this is how a wizard steps forward: the frame stays, the contents change.
 * `acts` replaces the sticky action row (omit to leave it, "" to empty it).
 * The message slot is cleared, since a new step is a clean slate.
 */
export function paintDrawer(body: string, opts: { acts?: string } = {}): void {
  if (!isDrawerOpen()) return;
  const b = document.getElementById("drawer-body");
  if (b) { b.innerHTML = body; b.scrollTop = 0; }
  if (opts.acts !== undefined) {
    const acts = document.getElementById("drawer-acts");
    if (acts) { acts.innerHTML = opts.acts; acts.hidden = !opts.acts; }
  }
  const msg = document.getElementById("drawer-msg");
  if (msg) msg.innerHTML = "";
}

/** Put a red line in the drawer's message slot. */
export function drawerFail(text: string): void {
  const m = document.getElementById("drawer-msg");
  if (m) m.innerHTML = `<p class="msg bad">${esc(text)}</p>`;
}

export function closeDrawer(): void {
  const back = document.getElementById(BACK);
  if (!back) return;
  teardown?.(); teardown = null;
  back.remove();
  const cb = onClose; onClose = null;
  cb?.();
}
